/**
 * PushAnalytics - Open-Rate / Click-Rate / Conversion-Rate-Dashboard
 * mit KI-Analyse via Claude Sonnet 4.5.
 *
 * Zeigt Top/Flop-Kampagnen + Empfehlungen. Wöchentlich kommt eine
 * Email an den konfigurierten Admin (Standard: info@strongfit.ch).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BarChart3, Sparkles, TrendingUp, TrendingDown, RefreshCw, Lightbulb, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RateBadge = ({ value, kind }) => {
  // kind: 'open' | 'click' | 'conv'
  // Schwellen pro KPI: Open >50% grün, Click >15% grün, Conv >5% grün
  const thresholds = { open: [50, 25], click: [15, 5], conv: [5, 1] };
  const [hi, mid] = thresholds[kind] || [50, 25];
  let cls = 'bg-red-100 text-red-700';
  if (value >= hi) cls = 'bg-green-100 text-green-700';
  else if (value >= mid) cls = 'bg-amber-100 text-amber-700';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>
      {value.toFixed(1)}%
    </span>
  );
};

const PushAnalytics = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const fetchAnalytics = useCallback(async (d = days) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/push/analytics?days=${d}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(r.data);
    } catch (e) {
      console.error('Analytics fetch error:', e);
      toast.error('Analytics konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchAnalytics(days); }, [days, fetchAnalytics]);

  const triggerAi = async () => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API}/push/analyze?days=${days}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Nach erfolgreichem Analyze die letzte Analyse nachladen
      setData(prev => prev ? { ...prev, last_ai_analysis: r.data } : prev);
      toast.success(
        r.data.source === 'claude'
          ? `KI-Analyse fertig (${r.data.sample_size || 0} Kampagnen)`
          : 'Analyse erstellt (Fallback-Modus)'
      );
    } catch (e) {
      console.error('AI analyze error:', e);
      toast.error('KI-Analyse fehlgeschlagen');
    } finally {
      setAnalyzing(false);
    }
  };

  const overall = data?.overall || {};
  const topCampaigns = data?.top || [];
  const flopCampaigns = data?.flop || [];
  const aiAnalysis = data?.last_ai_analysis;

  return (
    <Card data-testid="push-analytics-card">
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-purple-600" />
              Push-Analytics & KI-Empfehlungen
            </CardTitle>
            <CardDescription>
              Open-Rate, Click-Rate, Conversion + automatische Verbesserungs-Vorschläge
            </CardDescription>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="push-analytics-days">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Letzte 7 Tage</SelectItem>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => fetchAnalytics(days)}
              disabled={loading}
              variant="outline"
              size="sm"
              data-testid="push-analytics-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* KPI-Reihe */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500 uppercase font-semibold">Versendet</div>
            <div className="text-2xl font-bold text-gray-900">{overall.sent || 0}</div>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-xs text-blue-600 uppercase font-semibold">Geöffnet</div>
            <div className="text-2xl font-bold text-blue-900">{overall.opened || 0}</div>
            <div className="text-xs text-blue-700 mt-0.5">{(overall.open_rate || 0).toFixed(1)}%</div>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600 uppercase font-semibold">Geklickt</div>
            <div className="text-2xl font-bold text-purple-900">{overall.clicked || 0}</div>
            <div className="text-xs text-purple-700 mt-0.5">{(overall.click_rate || 0).toFixed(1)}%</div>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="text-xs text-green-600 uppercase font-semibold">Conversion</div>
            <div className="text-2xl font-bold text-green-900">{overall.converted || 0}</div>
            <div className="text-xs text-green-700 mt-0.5">{(overall.conversion_rate || 0).toFixed(1)}%</div>
          </div>
        </div>

        {/* Top + Flop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Top-Performer
            </h4>
            <div className="space-y-1.5">
              {topCampaigns.length === 0 && <p className="text-sm text-gray-400">Noch keine Daten</p>}
              {topCampaigns.map((c, i) => (
                <div key={i} className="p-2 bg-green-50 rounded border border-green-100 flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{c.title}</div>
                    <div className="text-xs text-gray-500">{c.delivered} zugestellt</div>
                  </div>
                  <RateBadge value={c.open_rate} kind="open" />
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-600" />
              Verbesserungs-Kandidaten
            </h4>
            <div className="space-y-1.5">
              {flopCampaigns.length === 0 && <p className="text-sm text-gray-400">Noch keine Daten</p>}
              {flopCampaigns.map((c, i) => (
                <div key={i} className="p-2 bg-red-50 rounded border border-red-100 flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{c.title}</div>
                    <div className="text-xs text-gray-500">{c.delivered} zugestellt</div>
                  </div>
                  <RateBadge value={c.open_rate} kind="open" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* KI-Empfehlungen */}
        <div className="border-t pt-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              KI-Analyse (Claude Sonnet 4.5)
            </h4>
            <div className="flex gap-2 items-center">
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                <Mail className="h-3 w-3" />
                Wöchentlich Mo 08:00 → info@strongfit.ch
              </Badge>
              <Button
                onClick={triggerAi}
                disabled={analyzing}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="push-analytics-ai-btn"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                {analyzing ? 'Analysiere...' : 'Jetzt analysieren'}
              </Button>
            </div>
          </div>

          {!aiAnalysis ? (
            <div className="p-6 bg-gray-50 rounded-lg border-dashed border-2 border-gray-200 text-center">
              <Sparkles className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                Noch keine Analyse vorhanden. Klick auf "Jetzt analysieren" oder warte auf den Wochenreport.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Summary */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="text-xs uppercase font-bold text-amber-800 mb-1">Zusammenfassung</div>
                <p className="text-sm text-gray-800">{aiAnalysis.summary}</p>
                <div className="mt-2 flex gap-2 text-xs text-amber-700">
                  <span>📊 {aiAnalysis.sample_size || 0} Kampagnen</span>
                  <span>·</span>
                  <span>🤖 {aiAnalysis.source}</span>
                  {aiAnalysis.created_at && (
                    <>
                      <span>·</span>
                      <span>{new Date(aiAnalysis.created_at).toLocaleString('de-CH')}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Insights */}
              {(aiAnalysis.insights || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase font-bold text-gray-600 mb-2 flex items-center gap-1">
                    <Lightbulb className="h-3 w-3" /> Was uns aufgefallen ist
                  </div>
                  <ul className="space-y-1.5">
                    {aiAnalysis.insights.map((i, idx) => (
                      <li key={idx} className="text-sm text-gray-700 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-amber-500">
                        {i}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Recommendations */}
              {(aiAnalysis.recommendations || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase font-bold text-gray-600 mb-2">🚀 Empfehlungen</div>
                  <div className="space-y-2">
                    {aiAnalysis.recommendations.map((r, idx) => (
                      <div key={idx} className="p-3 border-l-4 border-amber-400 bg-amber-50/40 rounded">
                        <div className="text-sm font-semibold text-gray-900 mb-1">{r.problem}</div>
                        <div className="text-sm text-gray-700 mb-2">💡 {r.suggestion}</div>
                        {(r.example_old || r.example_new) && (
                          <div className="space-y-1 text-xs">
                            {r.example_old && (
                              <div className="text-red-700">
                                ❌ <em>{r.example_old}</em>
                              </div>
                            )}
                            {r.example_new && (
                              <div className="text-green-700">
                                ✅ <strong>{r.example_new}</strong>
                              </div>
                            )}
                          </div>
                        )}
                        {r.expected_lift && (
                          <div className="mt-2 text-xs font-bold text-blue-700">
                            📈 Erwartung: {r.expected_lift}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PushAnalytics;
